import { useLocalSlice } from 'use-local-slice';

import type {
    CustomTranslationStatusId,
    ExportConfig,
    ExportEmpty,
    ExportNullAs,
    ExportSort,
    Indentation,
    JavaPropertiesEncoding,
    JavaPropertiesSeparator,
    PlaceholderFormat,
    PluralFormat,
    ProjectId,
    Webhook,
} from '../../types';
import { FileFormat } from '../../types';
import getDirectoryPrefixValue from '../../utils/getDirectoryPrefixValue';
import getZipStructure from '../../utils/getZipStructure';

const getExportEmptyValue = (fileFormat: FileFormat, exportEmpty: ExportEmpty): ExportEmpty => {
    if (fileFormat === FileFormat.srt || fileFormat === FileFormat.flutterSdk) {
        return 'skip';
    }

    if (exportEmpty === 'as_null') {
        return 'empty';
    }

    return exportEmpty;
};

const getExportNullAsValue = (fileFormat: FileFormat, exportNullAs: ExportNullAs): ExportNullAs => {
    if (fileFormat === FileFormat.yamlRuby) {
        return exportNullAs;
    }

    return 'empty';
};

const useDownloadReducer = (initialState: ExportConfig) =>
    useLocalSlice({
        slice: 'download',
        initialState,
        reducers: {
            updateFileFormat: (
                state,
                {
                    payload: {
                        value,
                        pluralFormatDefaults,
                        placeholderFormatDefaults,
                        indentationDefaults,
                        exportConfig,
                    },
                }: {
                    payload: {
                        value: {
                            fileFormat: FileFormat;
                            switchIncludeAll: boolean;
                        };
                        pluralFormatDefaults: Partial<Record<FileFormat, PluralFormat>>;
                        placeholderFormatDefaults: Partial<Record<FileFormat, PlaceholderFormat>>;
                        indentationDefaults: Partial<Record<FileFormat, Indentation>>;
                        exportConfig: ExportConfig;
                    };
                },
            ) => ({
                ...state,
                ...value,
                pluralFormat: pluralFormatDefaults?.[value.fileFormat],
                placeholderFormat: placeholderFormatDefaults?.[value.fileFormat],
                indentation: indentationDefaults?.[value.fileFormat],
                filterFilenames: [],
                switchCompact: value.fileFormat === FileFormat.arb ? true : state.switchCompact,
                switchIncludeDescription:
                    value.fileFormat === FileFormat.androidSdk ? false : state.switchIncludeDescription,
                switchIncludeComments: [FileFormat.androidSdk, FileFormat.arb].includes(value.fileFormat)
                    ? false
                    : state.switchIncludeComments,
                zipStructure: getZipStructure(value.fileFormat, exportConfig),
                directoryPrefix: getDirectoryPrefixValue(value.fileFormat, exportConfig),
                exportEmpty: getExportEmptyValue(value.fileFormat, state.exportEmpty),
                exportNullAs: getExportNullAsValue(value.fileFormat, state.exportNullAs),
                /* Start: Reset all app triggers */
                triggersAmazons3: false,
                triggersGcs: false,
                triggersGithub: false,
                triggersGithubEnterprise: false,
                triggersGitlab: false,
                triggersBitbucket: false,
                triggersBitbucketEnterprise: false,
                triggersAzure: false,
                defaultBitbucketReviewers: false,
                /* End: Reset all app triggers */
            }),
            updateLanguageList: (
                state: ExportConfig,
                {
                    payload,
                }: {
                    payload: {
                        languages: readonly string[];
                        customIso: {
                            [languageId: string]: number | string;
                        };
                    };
                },
            ) => {
                const { languages, customIso } = payload;

                state.exportLangs = languages;

                if (customIso && Object.entries(customIso).length > 0) {
                    state.customIsoCodes = {
                        ...state.customIsoCodes,
                        [state.fileFormat]: {
                            ...state.customIsoCodes?.[state.fileFormat],
                            ...customIso,
                        },
                    };
                }
            },
            updateFileAndFormatStructure: (
                state,
                {
                    payload,
                }: {
                    payload: {
                        filenamesCustom: boolean;
                        filenamesOriginal: boolean;
                        zipStructure: string;
                        directoryPrefix: string;
                    };
                },
            ) => ({
                ...state,
                ...payload,
            }),
            updateFilterData: (
                state,
                {
                    payload,
                }: {
                    payload: {
                        includeTags: string;
                        excludeTags: string;
                        filterFilenames: readonly string[];
                        customTranslationStatusIds: readonly CustomTranslationStatusId[];
                        exportAll: boolean;
                        exportGlastReviewed: boolean;
                        exportGnonfuzzy: boolean;
                        exportGnonhidden: boolean;
                        exportGproofread: boolean;
                        exportGtranslated: boolean;
                        exportGuntranslated: boolean;
                    };
                },
            ) => ({
                ...state,
                ...payload,
            }),
            updateTriggers: (
                state,
                {
                    payload,
                }: {
                    payload: {
                        triggers: {
                            triggersAmazons3: boolean;
                            triggersGcs: boolean;
                            triggersGithub: boolean;
                            triggersGithubEnterprise: boolean;
                            triggersGitlab: boolean;
                            triggersBitbucket: boolean;
                            triggersBitbucketEnterprise: boolean;
                            triggersAzure: boolean;
                        };
                        filterRepositories: string;
                        commitTemplate: string;
                        defaultBitbucketReviewers: boolean;
                    };
                },
            ) => {
                const { triggers, defaultBitbucketReviewers, ...restValue } = payload;

                return {
                    ...state,
                    ...triggers,
                    ...restValue,
                    defaultBitbucketReviewers,
                };
            },
            updateWebhooks: (
                state,
                {
                    payload,
                }: {
                    payload: {
                        switchWebhooks: boolean;
                        webhooks: readonly Webhook[];
                    };
                },
            ) => ({
                ...state,
                ...payload,
            }),
            updateOtherProjectsStrings: (
                state,
                {
                    payload,
                }: {
                    payload: {
                        exportPids: readonly ProjectId[];
                    };
                },
            ) => ({
                ...state,
                ...payload,
            }),
            updateOptions: (
                state,
                {
                    payload,
                }: {
                    payload: {
                        switchIncludeDescription: boolean;
                        switchIncludeComments: boolean;
                        switchNoLanguageFolders: boolean;
                        switchDisableReferences: boolean;
                        switchYamlIncludeRoot: boolean;
                        jsonUnescapedSlashes: boolean;
                        addNewlineEof: boolean;
                        replaceLineBreaks: boolean;
                        switchIcuNumeric: boolean;
                        exportEmpty: ExportEmpty;
                        exportNullAs: ExportNullAs;
                        exportSort: ExportSort;
                        pluralFormat: PluralFormat | undefined;
                        placeholderFormat: PlaceholderFormat | undefined;
                        switchEscapePercent: boolean;
                        indentation: Indentation | undefined;
                        javaPropertiesEncoding: JavaPropertiesEncoding;
                        javaPropertiesSeparator: JavaPropertiesSeparator;
                    };
                },
            ) => {
                const { fileFormat } = state;
                const { replaceLineBreaks, ...restValue } = payload;

                return {
                    ...state,
                    ...restValue,
                    replaceLineBreaks: {
                        ...state.replaceLineBreaks,
                        [fileFormat]: replaceLineBreaks,
                    },
                };
            },
        },
    });

export default useDownloadReducer;
